import { useState, useEffect, useCallback, useRef } from 'react';

interface CaretPosition {
  x: number;
  y: number;
  rect?: DOMRect;
}

// Global state para detectar se SmartChatWidget está ativo
let isSmartChatActive = false;
export const setSmartChatActive = (active: boolean) => {
  isSmartChatActive = active;
};

// Funções utilitárias para whitelist
function isInside(container: HTMLElement, node: Node | null): boolean {
  if (!node || !container) return false;
  const el = node.nodeType === 3 ? (node.parentElement as HTMLElement | null) : (node as HTMLElement | null);
  return !!(el && container.contains(el));
}

function isTextInput(el: Element | null): boolean {
  if (!el) return false;
  if ((el as HTMLElement).isContentEditable) return false; // editor real
  const tag = el.tagName?.toLowerCase();
  return tag === 'input' || tag === 'textarea';
}

// Helper para detectar se o foco está em elemento whitelisted
const isWhitelistedElement = (element: Element | null): boolean => {
  if (!element) return false;
  return !!(
    element.closest('[data-caso-search]') ||
    element.closest('[data-prompt-input]') ||
    element.closest('[data-doc-chat]')
  );
};

// Cleanup global para spans órfãos
const cleanupOrphanedSpans = () => {
  const orphanedSpans = document.querySelectorAll('span[data-caret-position="true"]');
  orphanedSpans.forEach(span => span.remove());
};

export const useCaretPosition = (containerRef: React.RefObject<HTMLElement>) => {
  const [position, setPosition] = useState<CaretPosition | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const rafRef = useRef<number>();
  const mutationObserverRef = useRef<MutationObserver>();

  const calculateCaretPosition = useCallback(() => {
    // GUARD 1: Não calcular se SmartChatWidget está ativo
    if (isSmartChatActive) {
      return null;
    }

    // GUARD 2: Cleanup de spans órfãos antes de prosseguir
    cleanupOrphanedSpans();

    if (!containerRef.current) return null;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    try {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      // Verificar se temos dimensões válidas
      if (rect.width === 0 && rect.height === 0) {
        // Para cursor colapsado, criar um elemento temporário para obter posição exata
        const span = document.createElement('span');
        span.setAttribute('data-caret-position', 'true'); // Marcador para cleanup
        span.style.opacity = '0';
        span.style.position = 'absolute';
        span.style.top = '-99999px';
        span.style.left = '-99999px';
        span.style.width = '0';
        span.style.height = '0';
        span.style.pointerEvents = 'none';
        span.style.zIndex = '-99999';
        span.style.visibility = 'hidden';
        span.style.overflow = 'hidden';
        span.textContent = '|';
        
        // Timeout de segurança para cleanup forçado (mais agressivo)
        const safetyTimeout = setTimeout(() => {
          if (span.parentNode) {
            span.remove();
          }
        }, 5);
        
        try {
          range.insertNode(span);
          const spanRect = span.getBoundingClientRect();
          
          // Cleanup imediato e seguro
          if (span.parentNode) {
            span.remove();
          }
          clearTimeout(safetyTimeout);
          
          return {
            x: spanRect.left - containerRect.left + 8,
            y: spanRect.top - containerRect.top,
            rect: spanRect
          };
        } catch (error) {
          // Fallback se inserção falhar
          if (span.parentNode) {
            span.remove();
          }
          clearTimeout(safetyTimeout);
          
          return {
            x: rect.left - containerRect.left + 8,
            y: rect.top - containerRect.top,
            rect
          };
        }
      }

      // Para seleção de texto, posicionar no final da seleção
      return {
        x: rect.right - containerRect.left + 8,
        y: rect.top - containerRect.top,
        rect
      };
    } catch (error) {
      console.error('Erro ao calcular posição do cursor:', error);
      // Cleanup de emergência
      cleanupOrphanedSpans();
      return null;
    }
  }, [containerRef]);

  const updatePosition = useCallback(() => {
    // GUARD: Não processar se SmartChatWidget está ativo
    if (isSmartChatActive) {
      return;
    }

    const newPosition = calculateCaretPosition();
    setPosition(newPosition);
    setIsVisible(newPosition !== null);
  }, [calculateCaretPosition]);

  const handleSelectionChange = useCallback(() => {
    // GUARD 0: Verificar se o foco atual está em elemento whitelisted
    const activeEl = document.activeElement;
    if (isWhitelistedElement(activeEl)) {
      return;
    }
    
    const container = containerRef.current;
    const sel = window.getSelection();

    // WHITELIST 1: Ignorar se não há seleção válida
    if (!container || !sel || sel.rangeCount === 0) return;

    // WHITELIST 2: Ignorar se o foco atual é input/textarea (ex.: chat)
    const active = document.activeElement;
    if (isTextInput(active)) return;

    // WHITELIST 3: Guard adicional para detectar chat/prompt inputs E BUSCA DE CASOS
    const node = (sel?.anchorNode?.nodeType === 3 ? sel?.anchorNode?.parentElement : sel?.anchorNode) as Element | null;
    if (node?.closest('[data-doc-chat]') || node?.closest('[data-prompt-input]') || node?.closest('[data-caso-search]')) {
      cleanupOrphanedSpans();
      return;
    }

    const range = sel.getRangeAt(0);

    // WHITELIST 4: **Só processe se a seleção estiver inteira dentro do editor**
    const insideStart = isInside(container, range.startContainer);
    const insideEnd = isInside(container, range.endContainer);
    if (!insideStart || !insideEnd) {
      cleanupOrphanedSpans(); 
      return; // <<< nada de criar span fora do editor
    }

    // Usar requestAnimationFrame para throttling suave
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    
    rafRef.current = requestAnimationFrame(() => {
      updatePosition();
    });
  }, [containerRef, updatePosition]);

  const handleKeyUp = useCallback(() => {
    updatePosition();
  }, [updatePosition]);

  const handleMouseUp = useCallback(() => {
    updatePosition();
  }, [updatePosition]);

  const handleScroll = useCallback(() => {
    updatePosition();
  }, [updatePosition]);

  const handleResize = useCallback(() => {
    updatePosition();
  }, [updatePosition]);

  const handleFocus = useCallback(() => {
    // GUARD: Não processar se foco está em elemento whitelisted
    const activeEl = document.activeElement;
    if (isWhitelistedElement(activeEl)) {
      return;
    }
    
    setIsVisible(true);
    updatePosition();
  }, [updatePosition]);

  const handleBlur = useCallback((e?: FocusEvent) => {
    // GUARD 1: Verificar se o novo foco é whitelisted
    const relatedTarget = e?.relatedTarget as HTMLElement | null;
    if (isWhitelistedElement(relatedTarget)) {
      return;
    }
    
    // GUARD 2: Verificar se o elemento atual que perdeu foco é whitelisted
    const currentTarget = e?.currentTarget as HTMLElement | null;
    if (isWhitelistedElement(currentTarget)) {
      return;
    }
    
    setIsVisible(false);
    setPosition(null);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Event listeners para o container
    container.addEventListener('keyup', handleKeyUp);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('focus', handleFocus);
    container.addEventListener('blur', handleBlur);
    container.addEventListener('scroll', handleScroll);

    // Event listeners globais
    document.addEventListener('selectionchange', handleSelectionChange);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('keyup', handleKeyUp);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('focus', handleFocus);
      container.removeEventListener('blur', handleBlur);
      container.removeEventListener('scroll', handleScroll);
      
      document.removeEventListener('selectionchange', handleSelectionChange);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [
    handleKeyUp,
    handleMouseUp,
    handleFocus,
    handleBlur,
    handleScroll,
    handleSelectionChange,
    handleResize
  ]);

  // MutationObserver para detectar e limpar spans órfãos (ANTI-FANTASMA)
  useEffect(() => {
    const killZWS = (root: ParentNode = document) => {
      // Limpar spans órfãos tradicionais
      root.querySelectorAll('span[data-caret-position="true"]').forEach(n => n.remove());
      // Limpar spans ZWS problemáticos
      root.querySelectorAll('span[style*="width: 0px"][style*="overflow: hidden"]').forEach(n => n.remove());
    };
    
    mutationObserverRef.current = new MutationObserver(muts => {
      for (const m of muts) {
        if (m.addedNodes && m.addedNodes.length) killZWS(document);
      }
    });

    // Observar mudanças no documento
    mutationObserverRef.current.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    // Cleanup periódico mais agressivo (100ms vs 500ms)
    const cleanupInterval = setInterval(() => {
      killZWS(document);
    }, 100);

    return () => {
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }
      clearInterval(cleanupInterval);
      killZWS(document); // Cleanup final
    };
  }, []);

  // Ajustar posição se estourar viewport
  const adjustedPosition = position ? {
    ...position,
    x: Math.min(position.x, window.innerWidth - 200), // Evitar overflow horizontal
    y: Math.max(position.y, 0) // Evitar overflow vertical negativo
  } : null;

  return {
    position: adjustedPosition,
    isVisible,
    updatePosition
  };
};